
import { useState } from 'react';
import type { StaffVoiceItem } from '../../../lib/wordpress';

interface StaffVoiceCardProps {
  staff: StaffVoiceItem;
}

const DEFAULT_IMAGE = 'https://public.readdy.ai/ai/img_res/3575f5c41f8f150585ee32c1290a5aae.jpg';

export default function StaffVoiceCard({ staff }: StaffVoiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const imageUrl =
    staff.image && staff.image !== 'false' && staff.image !== ''
      ? staff.image
      : DEFAULT_IMAGE;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
      <div className="p-5 flex flex-col gap-3">

        {/* タイトル */}
        <h3 className="text-base font-bold text-gray-900 leading-snug line-clamp-2">
          {staff.title}
        </h3>

        {/* 画像 + プロフィール */}
        <div className="flex items-center gap-3">
          {/* 円形画像 */}
          <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden border-2 border-blue-200">
            <img
              src={imageUrl}
              alt={staff.title}
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = DEFAULT_IMAGE;
              }}
            />
          </div>
          {/* プロフィール */}
          {staff.profile && (
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 flex-1">
              {staff.profile}
            </p>
          )}
        </div>

        {/* 勤務スタイル・月収 2カラム */}
        <div className="grid grid-cols-2 gap-2">
          {staff.schedule && (
            <div className="bg-gray-50 rounded-lg p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-teal-600 text-xs"></i>
                </div>
                <p className="text-xs text-gray-400">勤務スタイル</p>
              </div>
              <p
                className="text-xs font-semibold text-gray-800 leading-snug"
                dangerouslySetInnerHTML={{ __html: staff.schedule }}
              />
            </div>
          )}

          {staff.monthlyIncome && (
            <div className="bg-blue-50 rounded-lg p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-wallet-3-line text-blue-600 text-xs"></i>
                </div>
                <p className="text-xs text-gray-400">月収</p>
              </div>
              <p
                className="text-xs font-semibold text-blue-700 leading-snug"
                dangerouslySetInnerHTML={{ __html: staff.monthlyIncome }}
              />
            </div>
          )}
        </div>

        {/* アコーディオン詳細エリア */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pt-3 border-t border-gray-100 space-y-3">
            {/* 時給 */}
            {staff.hourlyRate && (
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-money-yen-circle-line text-teal-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">時給</p>
                  <p
                    className="text-sm font-medium text-gray-800"
                    dangerouslySetInnerHTML={{ __html: staff.hourlyRate }}
                  />
                </div>
              </div>
            )}

            {/* 実際の声 */}
            {staff.realVoice && (
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-chat-quote-line text-teal-600 text-sm"></i>
                  </div>
                  <p className="text-xs font-semibold text-gray-700">実際の声</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-3">
                  <div
                    className="text-xs text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: staff.realVoice }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 詳細ボタン */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center justify-center whitespace-nowrap cursor-pointer"
        >
          <span>{isExpanded ? '閉じる' : '詳細を見る'}</span>
          <div className="w-4 h-4 flex items-center justify-center ml-1.5">
            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-base`}></i>
          </div>
        </button>

      </div>
    </div>
  );
}
